import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useToast } from '../../../context/ToastContext'
import { CourseCard, CourseCardSkeleton, decorateCourse } from '@astro/course-card'
import { categoryEmoji } from './coursesData'
import { appUrl } from '@astro/config'
import { FiArrowRight, FiAward, FiMessageCircle, FiWifiOff, FiBookOpen } from 'react-icons/fi'
import { MdAllInclusive } from 'react-icons/md'
import { FaStar } from 'react-icons/fa6'

// Homepage preview: cap the grid at 4 cards (the API ignores `per_page`, so
// this is enforced client-side). The real total still drives "View All N".
const MAX_PREVIEW = 4

export default function CoursesCatalogSection() {
  const [filter, setFilter] = useState('all')
  const { showToast } = useToast()

  const {
    data: result,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['coursesList', 1],
    queryFn: () => api.courses.list({ page: 1 }),
  })

  // apiClient unwraps the axios envelope, so `result` is the response body:
  // { data: Course[], meta: { total }, links, success }.
  const courses = useMemo(
    () => (result?.data ?? []).map((c, i) => decorateCourse(c, i)),
    [result],
  )

  const total = result?.meta?.total ?? courses.length

  // Filter tabs come from the categories present in the live data.
  const filterTabs = useMemo(() => {
    const categories = [...new Set(courses.map((c) => c.category))]
    return [
      { key: 'all', label: 'All Courses' },
      ...categories.map((c) => ({ key: c, label: `${categoryEmoji(c)} ${c}` })),
    ]
  }, [courses])

  const visible = useMemo(() => {
    const list = filter === 'all' ? courses : courses.filter((c) => c.category === filter)
    return list.slice(0, MAX_PREVIEW)
  }, [courses, filter])

  return (
    <section id="courses-catalog" className="relative overflow-hidden md:px-12 bg-[#FAF7F2]">
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -right-40" style={{ width: 560, height: 560, background: 'radial-gradient(circle,rgba(200,146,42,.07) 0%,transparent 65%)', filter: 'blur(70px)', borderRadius: '50%' }} />
        <div className="absolute -bottom-40 -left-40" style={{ width: 480, height: 480, background: 'radial-gradient(circle,rgba(200,146,42,.05) 0%,transparent 65%)', filter: 'blur(60px)', borderRadius: '50%' }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.2),transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.12),transparent)' }} />
        <div className="absolute inset-0 opacity-[.018]" style={{ backgroundImage: 'radial-gradient(circle,#C8922A 1px,transparent 1px)', backgroundSize: '32px 32px' }} />
      </div>

      <div className="max-w-350 mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 pt-24 pb-14" style={{ borderBottom: '1px solid rgba(200,146,42,.12)' }}>
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <span className="font-cinzel text-base leading-none" style={{ color: '#C8922A' }}>ॐ</span>
              <div className="flex items-center gap-2">
                <div className="w-4 h-px" style={{ background: 'rgba(200,146,42,.4)' }} />
                <span className="text-[9px] uppercase font-bold tracking-[.45em] font-cinzel" style={{ color: 'rgba(200,146,42,.65)' }}>— Learn from Maharaj Ji —</span>
                <div className="w-4 h-px" style={{ background: 'rgba(200,146,42,.4)' }} />
              </div>
            </div>
            <h2 className="font-cinzel font-normal leading-none tracking-tight">
              <span className="block text-4xl sm:text-5xl lg:text-[52px]" style={{ color: '#1C0A03' }}>Certified</span>
              <span className="block text-4xl sm:text-5xl lg:text-[52px]" style={{ background: 'linear-gradient(90deg,#C8922A 0%,#E5A820 50%,#C8922A 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Astrology Courses
              </span>
            </h2>
            <p className="text-[14px] mt-4 max-w-sm leading-[1.85]" style={{ color: '#8B7355', fontWeight: 300 }}>
              Live online and offline classes in Astrology, Palmistry &amp; Vastu — made simple for beginners and experts.
            </p>
          </div>

          <div className="flex flex-col items-start lg:items-end gap-5 shrink-0">
            <div className="flex items-center gap-5">
              <div className="text-center">
                <p className="font-cinzel text-[22px] font-bold leading-none" style={{ color: '#1C0A03' }}>{isPending ? '—' : total}</p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Courses</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(200,146,42,.2)' }} />
              <div className="text-center">
                <p className="font-cinzel text-[22px] font-bold leading-none" style={{ color: '#1C0A03' }}>500+</p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Students</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(200,146,42,.2)' }} />
              <div className="text-center">
                <p className="font-cinzel text-[22px] font-bold leading-none inline-flex items-center gap-1" style={{ color: '#1C0A03' }}>4.9<FaStar className="w-4 h-4" style={{ color: '#C8922A' }} /></p>
                <p className="text-[8px] uppercase tracking-widest mt-1" style={{ color: '#9C8470' }}>Avg Rating</p>
              </div>
            </div>
            <a
              href={appUrl("education", "classes")}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-[10px] uppercase tracking-[.26em] font-bold transition-all duration-300"
              style={{ background: '#4A1A0A', color: '#FDE68A', boxShadow: '0 4px 20px rgba(74,26,10,.3)' }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#6B2510'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(74,26,10,.42)' }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#4A1A0A'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(74,26,10,.3)' }}
            >
              Explore All Courses
              <FiArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-7">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`cc-filter-tab${filter === tab.key ? ' cc-tab-active' : ''}`}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-20">
            {Array.from({ length: 4 }, (_, i) => <CourseCardSkeleton key={i} />)}
          </div>
        ) : isError ? (
          <div className="py-16 pb-24 text-center">
            <FiWifiOff className="w-12 h-12 mx-auto mb-4 opacity-40" style={{ color: '#C8922A' }} />
            <p className="font-cinzel text-lg font-bold mb-1" style={{ color: '#1C0A03' }}>Couldn’t Load Courses</p>
            <p className="text-sm mb-5" style={{ color: '#8B7355' }}>Something went wrong while fetching live data. Please try again.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : visible.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-20">
            {visible.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                href={appUrl('education', `classes/${course.slug ?? course.id}`)}
              />
            ))}
          </div>
        ) : (
          <div className="py-16 pb-24 text-center">
            <FiBookOpen className="w-12 h-12 mx-auto mb-4 opacity-40" style={{ color: '#C8922A' }} />
            <p className="font-cinzel text-lg font-bold mb-1" style={{ color: '#1C0A03' }}>No Courses Found</p>
            <p className="text-sm" style={{ color: '#8B7355' }}>Try a different category tab.</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pb-20 pt-2" style={{ borderTop: '1px solid rgba(200,146,42,.1)' }}>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)' }}><FiAward className="w-4 h-4" style={{ color: '#C8922A' }} /></div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Industry Recognised</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>Certificates</p>
              </div>
            </div>
            <div className="w-px h-8 hidden sm:block" style={{ background: 'rgba(200,146,42,.15)' }} />
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)' }}><MdAllInclusive className="w-4 h-4" style={{ color: '#C8922A' }} /></div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Lifetime Access</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>All Devices</p>
              </div>
            </div>
            <div className="w-px h-8 hidden sm:block" style={{ background: 'rgba(200,146,42,.15)' }} />
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,146,42,.1)' }}><FiMessageCircle className="w-4 h-4" style={{ color: '#C8922A' }} /></div>
              <div>
                <p className="text-xs font-bold" style={{ color: '#1C0A03' }}>Live Support</p>
                <p className="text-[8px] uppercase tracking-widest" style={{ color: '#9C8470' }}>24 × 7</p>
              </div>
            </div>
          </div>
          <a
            href={appUrl("education", "classes")}
            className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[.24em] font-bold px-7 py-3.5 rounded-full transition-all duration-300"
            style={{ border: '1.5px solid rgba(200,146,42,.28)', color: '#8B7355' }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.55)'; e.currentTarget.style.color = '#C8922A'; e.currentTarget.style.background = 'rgba(200,146,42,.05)' }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.28)'; e.currentTarget.style.color = '#8B7355'; e.currentTarget.style.background = 'transparent' }}
          >
            View All {isPending ? '' : `${total} `}Courses
            <FiArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  )
}
